import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { ENV_CONFIG } from 'config/environment';
import Icon, { IconNames } from 'components/Icon';
import { getIsRecipient, getIsSuper, has_permission } from 'config/permissions';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { trackEvent } from 'helpers/mixpanelEvents';
import {
  setClients,
  setSelectedClient,
  getClients,
  getSelectedClient,
  setSelectedRecipient,
  getSelectedRecipient,
} from 'services/storage';
import { setUserClientID, setUserClients } from 'store/user/actions';
import { getAdminsPartners, getAdminsState } from 'selectors/admins';
import colors from 'constants/colors';
import classNames from 'classnames';

const Sidebar = ({ routes, activeRole, subscriptionLevel }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const partners = useAppSelector(getAdminsPartners);
  const adminsState = useAppSelector(getAdminsState);
  const [collapseStates, setCollapseStates] = useState({});
  const { user } = useAuth0();

  const getDashDisplayName = () => {
    if (getIsRecipient(activeRole) && adminsState?.selectedRecipient?.name) {
      return adminsState?.selectedRecipient?.name;
    }
    if (!getIsRecipient(activeRole) && getSelectedClient()?.name) {
      return getSelectedClient()?.name;
    }
    return user?.name || 'Oath User';
  };

  // Sync Redux selectedRecipient with localStorage when it changes
  useEffect(() => {
    if (adminsState?.selectedRecipient && getIsRecipient(activeRole)) {
      const stored = getSelectedRecipient();
      // Only update localStorage if the Redux state differs from localStorage
      if (!stored || stored.id !== adminsState.selectedRecipient.id) {
        setSelectedRecipient(adminsState.selectedRecipient);
      }
    }
  }, [adminsState?.selectedRecipient, activeRole]);

  // Set clients from partners only when activeRole is available
  useEffect(() => {
    if (activeRole && partners && partners.length > 0 && getClients()?.length === 0) {
      const clientsFromPartners = partners.map(partner => ({
        id: partner.id,
        name: partner.name,
      }));

      setClients(clientsFromPartners);

      // Only set a default client if none is already stored
      const existingClient = getSelectedClient();
      if (!existingClient) {
        // For super admins, don't set a default client here - let AdminNavbar handle "ALL"
        if (!getIsSuper(activeRole)) {
          setSelectedClient(clientsFromPartners[0]);
          dispatch(setUserClientID(Number(clientsFromPartners[0].id)));
        }
      }

      dispatch(setUserClients(clientsFromPartners));
    }
  }, [activeRole, partners, dispatch]);

  const sidebar = useRef<any>();
  // this creates the intial state of this component based on the collapse routes
  // that it gets through props.routes
  const getCollapseStates = routes => {
    let initialState = {};
    routes.map(prop => {
      if (prop.collapse) {
        initialState = {
          [prop.state]: getCollapseInitialState(prop.children),
          ...getCollapseStates(prop.children),
          ...initialState,
        };
      }
      return null;
    });
    return initialState;
  };
  // this verifies if any of the collapses should be default opened on a rerender of this component
  // for example, on the refresh of the page,
  // while on the src/children/forms/RegularForms.js - route /admin/regular-forms
  const getCollapseInitialState = routes => {
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse && getCollapseInitialState(routes[i].children)) {
        return true;
      } else if (window.location.pathname.indexOf(routes[i].path) !== -1) {
        return true;
      }
    }
    return false;
  };

  // this function creates the links and collapses that appear in the sidebar (left menu)
  const createLinks = routes => {
    return routes.map((prop, key) => {
      if (prop.redirect || !has_permission(prop.permission, 'R', activeRole, subscriptionLevel)) {
        return null;
      }
      if (prop.collapse) {
        const st = {};
        st[prop['state']] = !collapseStates[prop.state];
        return (
          <li className={getCollapseInitialState(prop.children) ? 'active' : ''} key={key}>
            <a
              href="#"
              data-toggle="collapse"
              aria-expanded={collapseStates[prop.state]}
              onClick={e => {
                e.preventDefault();
                setCollapseStates(st);
              }}
            >
              {prop.icon !== undefined ? (
                <div className="row space-between">
                  <span>
                    <Icon
                      name={IconNames[prop.icon]}
                      className="sidebar-icon"
                      color={
                        prop.children.find(item => activeRoute(item.path) === 'active')
                          ? colors.electricBlue
                          : colors.white
                      }
                    />
                    {prop.name}
                  </span>
                  <Icon
                    name={collapseStates[prop.state] ? IconNames.ChevronUp : IconNames.ChevronDown}
                    className="sidebar-collapse-icon"
                    color={colors.inkBlue}
                    size={16}
                  />
                </div>
              ) : (
                <p>
                  {prop.name}
                  <Icon
                    name={collapseStates[prop.state] ? IconNames.ChevronUp : IconNames.ChevronDown}
                    className="sidebar-collapse-icon"
                    color={colors.inkBlue}
                    size={16}
                  />
                </p>
              )}
            </a>
            <div className={classNames('collapse', { show: collapseStates[prop.state] })}>
              <ul className="nav">{createLinks(prop.children)}</ul>
            </div>
          </li>
        );
      }
      return (
        <li className={`${activeRoute(prop.path)} sidebar-collapsed-li`} key={key}>
          <Link
            to={prop.path}
            onClick={() => trackEvent('Sidebar Item Clicked', { item: prop.path })}
          >
            {prop.icon !== undefined ? (
              <div className="row no-wrap">
                <Icon
                  name={IconNames[prop.icon]}
                  className="sidebar-icon"
                  color={activeRoute(prop.path) === 'active' ? colors.electricBlue : colors.white}
                />
                <span>
                  {prop.name === 'Dashboard' ? `${getDashDisplayName()}'s ${prop.name}` : prop.name}
                </span>
              </div>
            ) : (
              <div className="sidebar-collapsed-item">
                <p>{prop.name}</p>
              </div>
            )}
          </Link>
        </li>
      );
    });
  };
  // verifies if routeName is the one active (in browser input)
  const activeRoute = routeName => {
    // Handle both absolute paths (like '/dashboard') and relative paths (like 'dashboard')
    const currentPath = location.pathname;
    const routePath = routeName.startsWith('/') ? routeName : `/${routeName}`;

    // Exact match for the route
    if (currentPath === routePath) {
      return 'active';
    }

    // For nested routes, check if current path starts with the route path
    // e.g., '/donate_pages' should match '/donate_pages' but not '/donate_pages_detail'
    if (currentPath.startsWith(routePath + '/') || currentPath.startsWith(routePath + '?')) {
      return 'active';
    }

    return '';
  };

  useEffect(() => {
    setCollapseStates(getCollapseStates(routes));
  }, []);

  return (
    <div className="sidebar">
      <div className="logo">
        <div className="logo-img">
          <img
            src={`${ENV_CONFIG().CLOUD_STORAGE.URL}/static_content/oath_logos/Oath_FullLockup_White.png`}
            className="logo-img-file"
          />
        </div>
      </div>

      <div className="sidebar-wrapper" ref={sidebar}>
        <ul className="nav">{createLinks(routes)}</ul>
      </div>
    </div>
  );
};

export default Sidebar;
